<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/agent/dashboard">Clients</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">
        {{ app()?.entityName ?? 'Application' }}
      </span>
    </nav>
  </header>

  @if (isLoading()) {
    <div class="loading-state" aria-label="Loading application" aria-busy="true">
      <span class="sg-skeleton sg-skeleton--heading sg-skeleton--w40"></span>
      <span class="sg-skeleton sg-skeleton--text" style="width: 25%"></span>
      <span
        class="sg-skeleton"
        style="height: 96px; display: block; margin-top: var(--space-6)"
      ></span>
    </div>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load this application</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <div class="sg-empty__actions">
        <button mat-stroked-button type="button" (click)="load()">
          <app-icon name="refresh" [size]="18" /> Try again
        </button>
        <a mat-stroked-button routerLink="/agent/dashboard">Back to clients</a>
      </div>
    </div>
  } @else if (app(); as a) {
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">{{ a.entityName }}</h1>
        <p class="page-head__subtitle">
          <span class="agent-app__product" [attr.data-product]="a.productCode">{{
            a.productCode
          }}</span>
          {{ a.productLabel }}
          @if (a.amount) {
            · Applied for {{ a.amount | zar }}
          }
        </p>
      </div>
      <dl class="page-head__statuses">
        <div class="page-head__status">
          <dt>Facility</dt>
          <dd><app-status-chip [status]="a.facilityStatus" /></dd>
        </div>
        <div class="page-head__status">
          <dt>Application</dt>
          <dd><app-status-chip [status]="a.applicationStage" /></dd>
        </div>
      </dl>
    </div>

    <!-- ── Progress ─────────────────────────────────────────────────────── -->
    <!-- FLOW-402 (DOC-89-A §5): the stage row from the record, the same one
         the client and Ops see; none on Debtors Finance. The line under it is
         the client page's, by the position the back end reports. -->
    <section class="agent-app__card" aria-labelledby="progress-title">
      <h2 id="progress-title" class="agent-app__card-title">Progress</h2>
      @if (a.stageRow; as stageRow) {
        <app-stage-row [row]="stageRow" />
      }
      <p class="agent-app__next">
        <app-icon name="info" [size]="16" aria-hidden="true" />
        <span>{{ nextStep() }}</span>
      </p>
    </section>

    <!-- FLOW-344 (DOC-73 UI-DA-73-02) — the client page's own two panels, from
         the back end's list: the business's items with their acts (the agent
         acts for the business), and what CapX is working on. Kickstart only. -->
    @if (a.outstanding.length || a.taxPins.length) {
      <app-ks-outstanding
        [shortId]="a.shortId"
        [entityShortId]="a.entityShortId"
        [items]="a.outstanding"
        [taxPins]="a.taxPins"
        [taxPinOpen]="a.taxPinOpen"
        (changed)="load(true)"
      />
    }

    <!-- FLOW-224 (DOC-78 UI-DA-78-01) — the client's own first look, no more. -->
    @if (a.showsFirstLook && a.amount) {
      <app-first-look
        [firstLook]="a.firstLook"
        [amountRequested]="a.amount"
        [offerIssued]="a.offerIssued"
      />
    }

    <!-- ── Application details ───────────────────────────────────────────── -->
    <section class="agent-app__card" aria-labelledby="details-title">
      <h2 id="details-title" class="agent-app__card-title">Application details</h2>
      <dl class="facts">
        <div class="facts__row">
          <dt>Business</dt>
          <dd>{{ a.entityName }}</dd>
        </div>
        <div class="facts__row">
          <dt>Product</dt>
          <dd>{{ a.productLabel }}</dd>
        </div>
        <div class="facts__row" [class.facts__row--empty]="!a.detail">
          <dt>{{ a.detailLabel }}</dt>
          <dd>{{ a.detail | dash }}</dd>
        </div>
        <div class="facts__row" [class.facts__row--empty]="!a.submittedAt">
          <dt>Submitted</dt>
          <dd>{{ a.submittedAt ? (a.submittedAt | date: 'medium') : '—' }}</dd>
        </div>
      </dl>
    </section>

    <!-- ── Documents ─────────────────────────────────────────────────────── -->
    <section class="agent-app__card" aria-labelledby="docs-title">
      <h2 id="docs-title" class="agent-app__card-title">Supporting documents</h2>
      @if (a.documentNames.length > 0) {
        <ul class="doc-list">
          @for (name of a.documentNames; track $index) {
            <li class="doc-list__item">
              <app-icon name="document" [size]="16" />
              <span>{{ name }}</span>
            </li>
          }
        </ul>
      } @else {
        <p class="muted">No documents have been uploaded yet.</p>
      }
    </section>

    <!-- DOC-89 §4 (FLOW-391): the shared context blocks, to the agent for the
         business. Kickstart only; Debtors Finance waits. -->
    @if (a.productCode === 'KS') {
      <section class="agent-app__card" aria-label="Information supporting the application">
        <app-context-blocks-client subjectKind="KS_APPLICATION" [subjectId]="a.shortId" />
      </section>
    }

    <div class="agent-app__footer">
      <a class="agent-app__workspace" [routerLink]="a.workspaceLink">
        <span>Open in client workspace</span>
        <app-icon name="arrow-right" [size]="16" />
      </a>
    </div>
  }
</div>
